// src/components/ArtworkEditModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Box, Typography, IconButton, Grid, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { http } from "../lib/axios";
import subcategorySchema from '../utils/subcategorySchema';

export default function ArtworkEditModal({ open, onClose, artwork, onUpdated }) {
  const [title, setTitle] = useState(artwork.title);
  const [description, setDescription] = useState(artwork.description);
  const [price, setPrice] = useState(artwork.price);
  const [artworkType, setArtworkType] = useState(artwork.artwork_type);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState(artwork.images || []);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [subcategories, setSubcategories] = useState(artwork.subcategories || {});
  const [dimensions, setDimensions] = useState(artwork.dimensions || '');
  const [representationMode, setRepresentationMode] = useState(artwork.representation_mode || 'artflow');
  const [representationModeChangeReason, setRepresentationModeChangeReason] = useState('');

  useEffect(() => {
    if (artwork) {
      setTitle(artwork.title);
      setDescription(artwork.description);
      setPrice(artwork.price);
      setArtworkType(artwork.artwork_type);
      setExistingImages(artwork.images || []);
      setDeletedImageIds([]);
      setImages([]);
      setSubcategories(artwork.subcategories || {});
      setDimensions(artwork.dimensions || '');
      setRepresentationMode(artwork.representation_mode || 'artflow');
      setRepresentationModeChangeReason('');
    }
  }, [artwork]);

  const handleImageDelete = (imgUrl) => {
    const imgObj = existingImages.find(img => img.image === imgUrl);
    if (imgObj) {
      setDeletedImageIds(prev => [...prev, imgObj.id]);
      setExistingImages(prev => prev.filter(img => img.id !== imgObj.id));
    }
  };

  const handleFileChange = (e) => {
    setImages(Array.from(e.target.files));
  };

  const handleSubcategoryChange = (category, value) => {
    setSubcategories(prev => ({ ...prev, [category]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('price', String(price));
    formData.append('artwork_type', artworkType);
    formData.append('subcategories', JSON.stringify(subcategories));
    formData.append('dimensions', dimensions);
    formData.append('representation_mode', representationMode);
    if (representationMode === 'gallery_represented' && (artwork.representation_mode || 'artflow') === 'artflow') {
      formData.append('representation_mode_change_reason', representationModeChangeReason);
    }

    images.forEach((img) => formData.append('images', img));
    deletedImageIds.forEach((id) => formData.append('deleted_images', id));

    try {
      // If you need a CSRF cookie for other endpoints, you can ensure once at app init.
      // With OAuth-only auth classes, CSRF typically isn't required.
      const res = await http.patch(`/artworks/${artwork.id}/`, formData, {
        // DO NOT set Content-Type; axios will set the correct boundary automatically.
        // headers: { 'Content-Type': 'multipart/form-data' }  // <-- remove
        // Authorization will be attached by your axios interceptor if configured.
      });

      if (onUpdated) onUpdated(res.data);
      onClose();
    } catch (err) {
      console.error('Update failed', err);
      alert('Failed to update artwork');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Artwork</DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Price"
          fullWidth
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <TextField
          margin="dense"
          label="Artwork Type"
          fullWidth
          select
          value={artworkType}
          onChange={(e) => {
            setArtworkType(e.target.value);
            setSubcategories({});
          }}
        >
          {Object.keys(subcategorySchema).map((type, idx) => (
            <MenuItem key={idx} value={type}>{type}</MenuItem>
          ))}
        </TextField>

        {subcategorySchema[artworkType] && Object.entries(subcategorySchema[artworkType]).map(([category, options]) => (
          <TextField
            key={category}
            select
            margin="dense"
            label={category}
            fullWidth
            value={subcategories[category] || ''}
            onChange={(e) => handleSubcategoryChange(category, e.target.value)}
          >
            {options.map((opt, i) => (
              <MenuItem key={i} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        ))}

        <TextField
          margin="dense"
          label="Dimensions"
          fullWidth
          placeholder="e.g. 20x30x10 cm"
          value={dimensions}
          onChange={(e) => setDimensions(e.target.value)}
        />

        <TextField
          margin="dense"
          label="Representation Mode"
          fullWidth
          select
          value={representationMode}
          onChange={(e) => setRepresentationMode(e.target.value)}
        >
          <MenuItem value="artflow">ARTflow</MenuItem>
          <MenuItem value="gallery_represented">Gallery Represented</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Gallery represented artworks stay out of normal ARTflow hosting requests and buyer purchase until a gallery host formally claims them through an accepted representation agreement.
        </Typography>
        {representationMode === 'gallery_represented' && (artwork.representation_mode || 'artflow') === 'artflow' && (
          <TextField
            margin="dense"
            label="Reason for switching to Gallery Represented"
            fullWidth
            multiline
            minRows={2}
            value={representationModeChangeReason}
            onChange={(e) => setRepresentationModeChangeReason(e.target.value)}
          />
        )}

        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle2">Existing Images</Typography>
          <Grid container spacing={1}>
            {existingImages.map((img, idx) => (
              <Grid item key={idx}>
                <Box sx={{ position: 'relative' }}>
                  <img
                    src={img.image}
                    alt={`img-${idx}`}
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleImageDelete(img.image)}
                    sx={{ position: 'absolute', top: -10, right: -10, color: 'red' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2">Upload New Images</Typography>
          <input type="file" multiple onChange={handleFileChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
